import React, {FC, PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';

import {markFullStorySensitive} from '../../../helpers/fullstoryHelpers';
import {addCellsCommonStyles, makeCellClassName} from './cellBaseCommon';

/*
 * Props.
 */

interface CellBaseProps {
  className?: string;
  isSelected?: boolean;
  isPreview?: boolean;
  isTransparentPreview?: boolean;
  hasDoubleBottomRight?: boolean;
  hasDoubleTopLine?: boolean;
}

/*
 * Style.
 */

interface GridStyleProps {
  $isPreview?: boolean;
  $isTransparentPreview?: boolean;
  $hasDoubleBottomRight: boolean;
  $hasDoubleTopLine: boolean;
}

const StyledCellBaseGridDiv = styled.div<GridStyleProps>`
  display: grid;
  align-items: center;
  position: relative;
  padding: 10px 12px;

  grid-row-gap: 2px;
  grid-column-gap: 6px;
  grid-template-columns: 16px 1fr max-content 16px;
  grid-template-rows: repeat(3, 20px);
  grid-template-areas: ${(p) => addGridTemplateStyles(p.$hasDoubleBottomRight, p.$hasDoubleTopLine)};
  ${(p) => addAdditionalStyles(p)};
`;

function addGridTemplateStyles(hasDoubleBottomRight: boolean, hasDoubleTopLine: boolean) {
  return `'top-left top-line top-${hasDoubleTopLine ? 'line' : 'date'} top-right'
  'middle-left middle-line middle-line middle-line'
  'bottom-left bottom-line bottom-${hasDoubleBottomRight ? 'right' : 'line'} bottom-right'`;
}

function addAdditionalStyles(props: GridStyleProps) {
  if (props.$isTransparentPreview) {
    return undefined;
  }
  if (props.$isPreview) {
    return css`
      background-color: ${(p) => p.theme.cell.conversationPreviewBackground};
    `;
  }

  return css`
    margin: 8px 8px 0px 14px;
    border-radius: 8px;
    ${addCellsCommonStyles(false)}
  `;
}

/*
 * Component.
 */

export const CellBase: FC<PropsWithChildren<CellBaseProps>> = (props) => {
  // We need a custom class when selected to help style the next item.
  const {className: baseClassName, isSelected, children} = props;

  const className = makeCellClassName(baseClassName, isSelected);

  return (
    <StyledCellBaseGridDiv
      $isPreview={props.isPreview}
      $isTransparentPreview={props.isTransparentPreview}
      $hasDoubleBottomRight={Boolean(props.hasDoubleBottomRight)}
      $hasDoubleTopLine={Boolean(props.hasDoubleTopLine)}
      className={markFullStorySensitive(className)}
    >
      {children}
    </StyledCellBaseGridDiv>
  );
};
